import { SagaIterator } from 'redux-saga';
import {
  call,
  all,
  takeLatest,
  put,
  select,
  takeEvery,
} from 'redux-saga/effects';
import get from 'lodash/get';
import {
  editDeviation,
  deleteDeviation,
  deleteDeviationSuccess,
  fetchDeviation,
  unpublishDeviation,
  unpublishDeviationSuccess,
  OnUnpublishCompleteAction,
} from './actionCreators';
import { pushModal } from '../../Modals/redux/basicActions';
import { ModalType } from '../../Modals/redux/types';

import {
  getDeviationType,
  getDeviationUrl,
} from '../../DeviationViews/_helpers';
import { DeviationTypes, DeviationResourceType } from '../../types/deviation';
import {
  type PapiDaSharedDeviationUnpublish,
  PapiDeviation,
  PapiExtendedDeviation,
} from '@wix/da-papi-types';
import { normalize } from '../normalizr';
import {
  deviationExtendedSchema,
  deviationSchema,
  getCompositeId,
} from '../normalizr/schemas/deviation';
import { putEntities } from '../entities/actions';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { getIsLoggedIn } from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import ddt from '@wix/da-ddt';
import {
  getDeviationById,
  getDeviationEditUrl,
  getIsCurrentUserAuthorOf,
  getNormalizedDeviationById,
} from './selectors';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { publishCommentSuccess as publishCommentSuccessNew } from '../../CommentEditor/redux/actionCreators';
import { getDeviationTypeForCommentableType } from '../../DeviationPage/redux/helpers';
import { NormalizedDeviation } from '../normalizr/types';
import { getDataEntityById } from '../entities/selectors';
import { awardBadgeSuccess } from '../../coreBadge/redux/actionCreators';
import { putErrorNotification } from '../../utils/saga';
import i18n from 'i18next';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
const logger = ddt.createLogger('deviationsaga');

function fakeResponse(options): any {
  logger.log('faking the response!');
  if (process.env.NODE_ENV !== 'production') {
    if (typeof window !== 'undefined' && window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    if (options && options.url === '/deviation/fetch') {
      // eslint-disable-next-line
      const { deviationsByType } = require('@wix/da-sample-data');
      return {
        ...deviationsByType.image[0],
        ...{ deviationId: options.params.deviationid },
      };
    }
    return {
      success: true,
    };
  }
}

function* doPostPapiRequest(endpoint: string, data?: any) {
  logger.log(`calling ${endpoint} endpoint`);
  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: endpoint,
      data,
    },
    fakeResponse
  );
}

function* doGetPapiRequest(endpoint: string, params?: any) {
  logger.log(`calling ${endpoint} endpoint`);
  return yield call(
    requestPuppy,
    {
      method: 'get',
      url: endpoint,
      params,
    },
    fakeResponse
  );
}

function* deleteDeviationSaga({
  payload: { deviation, shouldRedirect, redirectTo },
}: ReturnType<typeof deleteDeviation>) {
  if (!deviation) {
    logger.error('Tried to delete an invalid deviation', deviation);
    return;
  }

  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.DELETE_DEVIATION })
    );
    return;
  }
  const isOwner = yield select(getIsCurrentUserAuthorOf, deviation);
  const canDelete = deviation.extended?.canUserDelete ?? isOwner;
  if (!canDelete) {
    logger.log('Cannot delete deviation', deviation, isOwner);
    return;
  }

  let endpoint, params;
  const deviationType = getDeviationType(deviation);
  const deviationId = deviation.deviationId;
  const userid = deviation.author && deviation.author.userId;
  switch (deviationType) {
    case DeviationTypes.STATUS:
      endpoint = '/status/delete';
      params = {
        statusid: deviationId,
        userid,
      };
      break;
    case DeviationTypes.POLL:
      endpoint = '/poll/delete';
      params = {
        pollid: deviationId,
        userid,
      };
      break;
    case DeviationTypes.COMMISSION:
      endpoint = '/commission/delete';
      params = {
        commissionid: deviationId,
      };
      break;
    case DeviationTypes.TIER:
      endpoint = '/tier/delete';
      params = { tierid: deviationId };
      break;
    default:
      endpoint = '/deviation/delete';
      params = {
        deviationid: deviationId,
      };
  }
  const result = yield call(doPostPapiRequest, endpoint, params);
  if (result && !isApiError(result)) {
    if (shouldRedirect || redirectTo) {
      typeof window !== 'undefined' &&
        window.location.assign(
          redirectTo ??
            (deviation.author
              ? Url.userLink(deviation.author.username)
              : URLS.ROOT)
        );
      return;
    }

    const deletedDeviation = { ...deviation, isDeleted: true };
    const { entities } = normalize(deletedDeviation, deviationSchema);
    yield all([
      put(putEntities({ entities })),
      put(deleteDeviationSuccess({ deviation: deletedDeviation })),
    ]);
  }
}

function* getExtendedDeviation(
  deviation: PapiDeviation,
  forceFetch: boolean = false
) {
  const isExpanded = !!get(deviation, 'textContent.html.markup');
  if (!forceFetch && isExpanded) {
    logger.log('Deviation already expanded', deviation);
    return { deviation };
  }

  const deviationType = getDeviationType(deviation);
  let result;
  switch (deviationType) {
    case DeviationTypes.STATUS:
    case DeviationTypes.LITERATURE:
    case DeviationTypes.JOURNAL:
    case DeviationTypes.COMMISSION:
    case DeviationTypes.POLL:
      const deviationIdPropName = [
        DeviationTypes.JOURNAL,
        DeviationTypes.LITERATURE,
      ].includes(deviationType)
        ? 'deviationid'
        : `${deviationType}id`;
      logger.log('Deviation PropName', deviationIdPropName, deviation);
      result = yield call(requestPuppy, {
        method: 'get',
        url: `/${deviationType}`,
        params: {
          [deviationIdPropName]: deviation.deviationId,
          username: deviation.author!.username,
          expand: 'deviation.fulltext',
        },
      });
      if (deviationType === DeviationTypes.JOURNAL) {
        // Forcibly add isFeaturedJournal based on what came back
        result.deviation.extended = {
          ...(result.deviation.extended || {}),
          isFeaturedJournal: result.featured,
        };
      }
      break;
    case DeviationTypes.TIER:
      result = yield call(requestPuppy, {
        method: 'get',
        url: `/${deviationType}`,
        params: {
          deviationid: deviation.deviationId,
          username: deviation.author!.username,
        },
      });
      break;

    default:
      return deviation;
  }

  // TODO error handling
  if (!result || isApiError(result)) {
    return;
  }

  logger.log('Extended deviation', result.deviation);
  const { entities } = normalize(result.deviation, deviationSchema);
  yield put(putEntities({ entities }));
  return result;
}

interface ParamsProps {
  deviation: PapiDeviation;
  isFeatured?: boolean;
  inTiers?: PapiDeviation[];
}

/**
 * Listen to editDeviation actions and fire the
 * correct edit deviation action depending on the deviation type.
 */
function* editDeviationSaga({
  payload: { deviation },
}: ReturnType<typeof editDeviation>) {
  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.EDIT_DEVIATION })
    );
    return;
  }

  // TODO: this allows group members to edit deviations where they have permission,
  // but it requires the full deviation object, otherwise just falls back to author equality
  // this works for now but when we implement group profiles will need to tweak it
  const isOwner = yield select(getIsCurrentUserAuthorOf, deviation);
  const canUserEdit: boolean = get(deviation, 'extended.canUserEdit', isOwner);

  if (!canUserEdit) {
    logger.log(
      'Attempted to edit a deviation that is not owned by you.',
      deviation
    );
    return;
  }

  const editUrl = getDeviationEditUrl(deviation);
  if (editUrl) {
    return typeof window !== 'undefined' && window.location.assign(editUrl);
  }

  const { isTextEditable, legacyTextEditUrl, extended } = deviation;

  const deviationType = getDeviationType(deviation);
  const needInTiersInfo = [
    DeviationTypes.TIER,
    DeviationTypes.JOURNAL,
    DeviationTypes.STATUS,
  ].includes(deviationType);
  const needExtendedInfo = isTextEditable || needInTiersInfo;
  if (needExtendedInfo) {
    const expandedDeviationResult = yield call(
      getExtendedDeviation,
      deviation,
      !extended || needInTiersInfo
    );

    const params: ParamsProps = {
      deviation: expandedDeviationResult.deviation,
    };

    switch (deviationType) {
      case DeviationTypes.STATUS:
        params.inTiers = expandedDeviationResult.inTiers;
        yield put(
          pushModal(ModalType.STATUS_UPDATE, {
            wrapInModal: false,
            params,
          })
        );
        break;
      case DeviationTypes.JOURNAL:
        params.inTiers = expandedDeviationResult.inTiers;
        params.isFeatured =
          expandedDeviationResult.featured ||
          get(params, 'deviation.extended.isFeaturedJournal', false);
        yield put(
          pushModal(ModalType.JOURNAL, {
            wrapInModal: false,
            params,
          })
        );
        break;
      case DeviationTypes.LITERATURE:
        yield put(
          pushModal(ModalType.LITERATURE, {
            wrapInModal: false,
            params,
          })
        );
        break;
      case DeviationTypes.COMMISSION:
        yield put(
          pushModal(ModalType.COMMISSION, {
            wrapInModal: false,
            params,
          })
        );
        break;
      case DeviationTypes.POLL:
        yield put(
          pushModal(ModalType.POLL_EDIT, {
            wrapInModal: false,
            params,
          })
        );
        break;
      case DeviationTypes.TIER:
        yield put(
          pushModal(ModalType.TIER_EDIT, {
            wrapInModal: false,
            params: expandedDeviationResult,
          })
        );
        break;
      default:
    }
    return;
  }
  if (legacyTextEditUrl) {
    return (
      typeof window !== 'undefined' && window.location.assign(legacyTextEditUrl)
    );
  }

  typeof window !== 'undefined' &&
    window.location.assign(getDeviationUrl(deviation));
}

export function* fetchDeviationSaga({
  payload: { deviationid, username, type, onSuccess },
}: ReturnType<typeof fetchDeviation>) {
  const result = yield call(doGetPapiRequest, '/deviation/fetch', {
    deviationid,
    username,
    type,
  });
  if (result && !isApiError(result)) {
    // Sometimes the deviation id in our payload is a stash private id.
    // This will still get us the papi_deviation, but its deviation_id won't be the private one.
    // Thus we get the id from the api response to lookup from the entities store and avoid a key mismatch.
    const deviationId = result.deviationId;
    const { entities } = normalize(result, deviationSchema);
    yield put(putEntities({ entities }));

    if (onSuccess) {
      const deviation = yield select(getDeviationById, deviationId);
      yield call(onSuccess, deviation);
    }
  }
}

// increment comment count on deviations when new comments are posted
function* handlePublishComment({
  payload: { isEdit, comment },
}: ReturnType<typeof publishCommentSuccessNew>) {
  if (isEdit || !comment) {
    return;
  }

  const deviationTypeId = getDeviationTypeForCommentableType(comment.typeId);
  const entityId = getCompositeId(deviationTypeId, comment.itemId);

  // stats are not normalized so we can just work with the normalized deviations
  const deviation: NormalizedDeviation = yield select(
    getNormalizedDeviationById,
    entityId
  );

  if (!deviation) {
    return;
  }

  const updatedDeviation: NormalizedDeviation = {
    ...deviation,
    stats: {
      favourites: 0,
      ...deviation.stats,
      comments: (deviation.stats?.comments ?? 0) + 1,
    },
    hasCommentedOn: true,
  };

  yield put(
    putEntities({
      entities: {
        [deviationSchema.key]: {
          [entityId]: updatedDeviation,
        },
      },
    })
  );
}

function* handleAwardBadge(action: ReturnType<typeof awardBadgeSuccess>) {
  const { recipient, allBadges } = action.payload;

  if (!(recipient.typeId in DeviationResourceType)) {
    return;
  }

  const deviation: PapiDeviation = yield select(
    getDataEntityById,
    deviationSchema,
    recipient.itemId
  );

  const extendedData: PapiExtendedDeviation = yield select(
    getDataEntityById,
    deviationExtendedSchema,
    recipient.itemId
  );

  if (!deviation || !extendedData) {
    return;
  }

  const updatedDeviation = { ...deviation };
  const { entities: deviationNormalized } = normalize(
    updatedDeviation,
    deviationSchema
  );

  yield put(
    putEntities({
      entities: {
        ...deviationNormalized,
        [deviationExtendedSchema.key]: {
          [recipient.itemId]: {
            ...extendedData,
            awardedBadges: allBadges,
          },
        },
      },
    })
  );
}

function* handleUnpublishDeviation(
  action: ReturnType<typeof unpublishDeviation>
) {
  const { deviation, onCompleteAction } = action.payload;

  const result: PapiDaSharedDeviationUnpublish = yield call(
    doPostPapiRequest,
    '/deviation/unpublish',
    {
      deviationid: deviation.deviationId,
    }
  );

  if (!result || isApiError(result)) {
    yield putErrorNotification(i18n.t('actionbar.unpublish.saga.error'));
    return;
  }

  yield put(
    unpublishDeviationSuccess({ deviation: result.deviation, onCompleteAction })
  );
}

function* handleUnpublishDeviationSuccess(
  action: ReturnType<typeof unpublishDeviationSuccess>
) {
  const { deviation, onCompleteAction } = action.payload;

  switch (onCompleteAction) {
    case OnUnpublishCompleteAction.Toast:
      const { entities } = normalize(deviation, deviationSchema);
      yield all([
        put(putEntities({ entities })),
        put(
          addNotification(NotificationType.Unpublish, {
            deviation,
          })
        ),
      ]);
      break;
    case OnUnpublishCompleteAction.ReloadStashPage:
    default:
      if (deviation.url) {
        window.location.href = deviation.url;
      }
  }
}

export default function* saga(): SagaIterator {
  yield all([
    takeLatest(editDeviation, editDeviationSaga),
    takeLatest(deleteDeviation, deleteDeviationSaga),
    takeLatest(fetchDeviation, fetchDeviationSaga),
    takeEvery(publishCommentSuccessNew, handlePublishComment),
    takeEvery(awardBadgeSuccess, handleAwardBadge),
    takeEvery(unpublishDeviation, handleUnpublishDeviation),
    takeEvery(unpublishDeviationSuccess, handleUnpublishDeviationSuccess),
  ]);
}
